// ----------------------------------------------------------------------
// Shared client-side file-upload validation.
//
// This is an ADDITIONAL layer in front of the API - the backend validates
// again. Its job is to stop obviously dangerous / wrong files before an
// upload request is ever sent, and to give the user a clear message.
//
// Do not rely on the file extension alone: we also check the browser
// reported MIME type, the extension/MIME consistency and the size.
// ----------------------------------------------------------------------

// Extensions that must never be uploaded anywhere, whatever the category.
export const DANGEROUS_EXTENSIONS = [
  // PHP / server-side templates
  "php", "php2", "php3", "php4", "php5", "php6", "php7", "php8",
  "phtml", "pht", "phps", "phar", "phpt", "inc",
  // Python / Ruby / Perl / other interpreters
  "py", "pyc", "pyo", "rb", "pl", "pm", "cgi", "lua", "tcl",
  // Shell / batch
  "sh", "bash", "zsh", "ksh", "csh", "run", "bat", "cmd", "com",
  "ps1", "psm1", "vbs", "vbe", "wsf", "wsh", "hta",
  // Browser-executable / script
  "js", "mjs", "cjs", "jsx", "ts", "tsx", "jse",
  // Java / .NET / native binaries
  "jar", "war", "ear", "class", "exe", "dll", "so", "dylib", "bin",
  "msi", "scr", "cpl", "app", "deb", "rpm", "apk", "dmg",
  // Other server pages
  "asp", "aspx", "ashx", "asmx", "jsp", "jspx", "cshtml", "vbhtml",
  // Markup that browsers render / execute
  "html", "htm", "xhtml", "shtml", "dhtml", "mht", "mhtml", "svgz",
  "xml", "xsl", "xslt", "swf",
  // Config / secrets / data dumps
  "htaccess", "htpasswd", "ini", "env", "conf", "config", "cnf", "cfg",
  "yml", "yaml", "toml", "sql", "sqlite", "db", "dump", "bak",
  "pem", "key", "crt", "p12", "pfx",
];

// MIME fragments that indicate code / executables / markup.
const DANGEROUS_MIME_FRAGMENTS = [
  "php", "x-httpd", "x-python", "x-perl", "x-ruby", "x-sh", "x-shellscript",
  "x-bash", "x-csh", "x-executable", "x-dosexec", "x-msdownload",
  "x-msdos-program", "x-mach-binary", "javascript", "ecmascript",
  "text/html", "application/xhtml", "x-java", "java-archive", "application/hta",
];

const MB = 1024 * 1024;

// Per-category allow lists. Keep these aligned with what each upload
// legitimately needs (and with the backend config/uploads.php).
export const UPLOAD_CATEGORIES = {
  // Photos / pictures - product images, profile & cover pictures, banners,
  // blog images, event images, lead visuals, referral image.
  image: {
    label: "image",
    extensions: ["jpg", "jpeg", "png", "webp", "gif"],
    mimes: ["image/jpeg", "image/pjpeg", "image/png", "image/webp", "image/gif"],
    maxBytes: 5 * MB,
  },

  // Logos / icons that legitimately also allow vector + favicon formats.
  imageSvg: {
    label: "image",
    extensions: ["jpg", "jpeg", "png", "webp", "gif", "svg", "ico"],
    mimes: [
      "image/jpeg", "image/pjpeg", "image/png", "image/webp", "image/gif",
      "image/svg+xml", "image/svg", "image/x-icon", "image/vnd.microsoft.icon",
      "text/plain", "text/xml", "application/xml",
    ],
    maxBytes: 5 * MB,
  },

  // Images and PDF files (support tickets, etc.)
  imagePdf: {
    label: "image or PDF",
    extensions: ["jpg", "jpeg", "png", "webp", "gif", "svg", "pdf"],
    mimes: [
      "image/jpeg", "image/pjpeg", "image/png", "image/webp", "image/gif",
      "image/svg+xml", "image/svg",
      "application/pdf",
    ],
    maxBytes: 10 * MB,
  },

  // Office / PDF documents - product docs & sample docs, tool documents,
  // material docs, business-builder docs.
  document: {
    label: "document",
    extensions: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"],
    mimes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/zip", "application/x-zip", "application/octet-stream",
      "text/plain", "text/csv",
    ],
    maxBytes: 10 * MB,
  },

  // KYC / identity / address proof.
  kyc: {
    label: "image or PDF",
    extensions: ["jpg", "jpeg", "png", "pdf"],
    mimes: ["image/jpeg", "image/pjpeg", "image/png", "application/pdf"],
    maxBytes: 10 * MB,
  },

  // Payment proof (bank transfer receipt etc.).
  paymentProof: {
    label: "image or PDF",
    extensions: ["jpg", "jpeg", "png", "webp", "pdf"],
    mimes: ["image/jpeg", "image/pjpeg", "image/png", "image/webp", "application/pdf"],
    maxBytes: 10 * MB,
  },

  // Support-ticket / email attachments - images + common documents.
  attachment: {
    label: "image or document",
    extensions: [
      "jpg", "jpeg", "png", "webp", "gif", "svg",
      "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv",
    ],
    mimes: [
      "image/jpeg", "image/pjpeg", "image/png", "image/webp", "image/gif",
      "image/svg+xml", "image/svg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/zip", "application/x-zip", "application/octet-stream",
      "text/plain", "text/csv",
    ],
    maxBytes: 10 * MB,
  },

  video: {
    label: "video",
    extensions: ["mp4", "mov", "webm", "ogg", "ogv", "m4v"],
    mimes: ["video/mp4", "video/quicktime", "video/webm", "video/ogg", "video/x-m4v"],
    maxBytes: 50 * MB,
  },
};

function humanSize(bytes) {
  if (bytes >= MB) return `${Math.round(bytes / MB)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

function getExtensionSegments(name) {
  return String(name || "")
    .toLowerCase()
    .split(".")
    .map((s) => s.replace(/[^a-z0-9]/g, ""))
    .filter(Boolean);
}

/**
 * Check if a value is a File-like object (File, Blob, or object with file attributes).
 * String URLs and primitive types return false.
 */
export function isFileLike(item) {
  if (!item) return false;
  if (typeof item === "string") return false;
  if (typeof File !== "undefined" && item instanceof File) return true;
  if (typeof Blob !== "undefined" && item instanceof Blob) return true;
  return Boolean(item.name || item.path) && typeof item.size === "number";
}

/**
 * Validate a single File against a category preset.
 * @param {File} file
 * @param {string} categoryKey  key of UPLOAD_CATEGORIES (default "image")
 * @returns {{ valid: boolean, message: string }}
 */
export function validateFile(file, categoryKey = "image") {
  const category = UPLOAD_CATEGORIES[categoryKey] || UPLOAD_CATEGORIES.image;

  if (!file) return { valid: false, message: "No file selected." };
  if (typeof file === "string") return { valid: true, message: "" };
  if (!isFileLike(file)) return { valid: true, message: "" };

  const name = file.name || file.path || "";
  const segments = getExtensionSegments(name);

  // 1. No dangerous component anywhere in the name ("shell.php.jpg").
  for (let i = 1; i < segments.length; i += 1) {
    if (DANGEROUS_EXTENSIONS.indexOf(segments[i]) !== -1) {
      return { valid: false, message: "This file type is not allowed." };
    }
  }

  const ext = segments.length ? segments[segments.length - 1] : "";
  const mime = String(file.type || "").toLowerCase();

  // 2. Dangerous MIME type regardless of extension.
  for (let i = 0; i < DANGEROUS_MIME_FRAGMENTS.length; i += 1) {
    if (mime && mime.indexOf(DANGEROUS_MIME_FRAGMENTS[i]) !== -1) {
      return { valid: false, message: "This file type is not allowed." };
    }
  }

  // 3. Extension must be in the category allow list.
  if (!ext || category.extensions.indexOf(ext) === -1) {
    return {
      valid: false,
      message: `Please upload a valid ${category.label}.`,
    };
  }

  // 4. Extension / MIME consistency (only when the browser reported a MIME).
  if (mime && category.mimes && category.mimes.indexOf(mime) === -1) {
    const imageExt = ["jpg", "jpeg", "png", "webp", "gif", "ico"].indexOf(ext) !== -1;
    if (!(imageExt && mime.indexOf("image/") === 0)) {
      return { valid: false, message: "This file type is not allowed." };
    }
  }

  // 5. Size limit.
  if (category.maxBytes && file.size > category.maxBytes) {
    return {
      valid: false,
      message: `File is too large. Maximum size is ${humanSize(category.maxBytes)}.`,
    };
  }

  return { valid: true, message: "" };
}

/**
 * Validate a list / FileList of files.
 * @returns {{ valid: boolean, message: string, validFiles: File[] }}
 */
export function validateFiles(files, categoryKey = "image", options = {}) {
  const list = files ? Array.from(files) : [];
  const { maxFiles } = options;

  if (maxFiles && list.length > maxFiles) {
    return {
      valid: false,
      message: `You can upload up to ${maxFiles} file${maxFiles > 1 ? "s" : ""}.`,
      validFiles: [],
    };
  }

  for (let i = 0; i < list.length; i += 1) {
    const result = validateFile(list[i], categoryKey);
    if (!result.valid) {
      return { valid: false, message: result.message, validFiles: [] };
    }
  }

  return { valid: true, message: "", validFiles: list };
}

/**
 * Extract a single File from a value that may be a FileList, an array,
 * a File, a URL string, or empty.
 */
export function firstFileOf(value) {
  if (!value) return null;
  if (typeof value === "string") return null;
  if (isFileLike(value)) return value;
  if (Array.isArray(value) || (typeof value === "object" && typeof value.length === "number")) {
    for (let i = 0; i < value.length; i += 1) {
      if (isFileLike(value[i])) return value[i];
    }
    return null;
  }
  return null;
}

/**
 * Yup `.test(...)` config that enforces the category allow list on a
 * file field. Presence ("required") is left to the caller's own rules.
 *
 *   Yup.mixed().test(yupFileTest("kyc"))
 */
export function yupFileTest(categoryKey) {
  return {
    name: "file-type-allowed",
    test(value, ctx) {
      if (!value) return true;
      if (typeof value === "string") return true;

      // Single file
      if (isFileLike(value)) {
        const result = validateFile(value, categoryKey);
        return result.valid || ctx.createError({ message: result.message });
      }

      // Array or FileList of files / string URLs
      if (Array.isArray(value) || (typeof value === "object" && typeof value.length === "number")) {
        const list = Array.from(value);
        for (let i = 0; i < list.length; i += 1) {
          const item = list[i];
          if (isFileLike(item)) {
            const result = validateFile(item, categoryKey);
            if (!result.valid) {
              return ctx.createError({ message: result.message });
            }
          }
        }
        return true;
      }

      return true;
    },
  };
}

/**
 * react-dropzone per-file validator. Returns null when OK, or a
 * { code, message } object when the file must be rejected.
 */
export function dropzoneValidator(categoryKey = "image") {
  return (file) => {
    const result = validateFile(file, categoryKey);
    if (result.valid) return null;
    return { code: "file-not-allowed", message: result.message };
  };
}

/**
 * Value for a native <input type="file"> `accept` attribute.
 */
export function acceptAttr(categoryKey = "image") {
  const category = UPLOAD_CATEGORIES[categoryKey] || UPLOAD_CATEGORIES.image;
  return category.extensions.map((e) => `.${e}`).join(",");
}

/**
 * Guard for a native <input type="file"> change event.
 *
 * Validates the selected file(s); on failure it notifies the user, clears
 * the input so the bad file is never submitted, and returns null. On
 * success it returns the File (single) or File[] (multiple) so the caller
 * can continue its existing flow unchanged.
 *
 * @param {Event} event      the input change event
 * @param {string} categoryKey  key of UPLOAD_CATEGORIES
 * @param {(msg: string) => void} notify  shows the error message to the user
 * @param {{ multiple?: boolean, maxFiles?: number }} [options]
 * @returns {File | File[] | null}
 */
export function guardFileEvent(event, categoryKey, notify, options = {}) {
  const input = event && event.target;
  const files = input && input.files ? Array.from(input.files) : [];
  if (!files.length) return options.multiple ? [] : null;

  if (options.multiple) {
    const result = validateFiles(files, categoryKey, { maxFiles: options.maxFiles });
    if (!result.valid) {
      if (typeof notify === "function") notify(result.message);
      try { input.value = ""; } catch (e) { /* noop */ }
      return null;
    }
    return result.validFiles;
  }

  const result = validateFile(files[0], categoryKey);
  if (!result.valid) {
    if (typeof notify === "function") notify(result.message);
    try { input.value = ""; } catch (e) { /* noop */ }
    return null;
  }
  return files[0];
}
